import {
  type FormEvent, type ReactElement, type ReactNode, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  generatePath, Navigate, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Modal from 'components/Modal/Modal';
import Panel from 'components/Panel/Panel';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';

import {
  currentCollectionRows,
  fieldChange,
  reviewCollectionRows,
} from './companyInvestigation';
import downloadJsonFile from './downloadJsonFile';
import { watchRequestErrorText } from './investigationRuns';
import {
  TargetedMarketingGate,
  TargetedMarketingHeader,
} from './TargetedMarketingChrome';
import useWatchTowerMock from './useWatchTowerMock';
import { toWatchCompanyImport } from './watchCompanyExport';
import {
  isInvestigationConclusion,
  relatedWatchCompanyIdFromFinding,
  watchTowerKind,
} from './watchInvestigation';
import InvestigationField from './WatchInvestigationDiff';
import WatchTowerDossierChatDialog from './WatchTowerDossierChatDialog';
import WatchTowerInvestigationRuns from './WatchTowerInvestigationRuns';
import { formatAgentType, formatIsoDate } from './watchTowerSelectors';

import type { WatchReviewRow } from './companyInvestigation';
import type { IInvestigationStepAttachment } from './investigationStepChat';
import type { WatchCompany, WatchCompanyStatus, WatchIdentifier } from './types';

import styles from './scss/TargetedMarketing.module.scss';

function rowDiffClass(op: string): string | undefined {
  if (op === 'add') {
    return styles.rowAdded;
  }
  if (op === 'remove') {
    return styles.rowRemoved;
  }
  return undefined;
}

function ReviewCell({
  row,
  field,
  children,
}: {
  row: WatchReviewRow<unknown>,
  field: string,
  children: ReactNode,
}): ReactElement {
  if (row.op === 'update' && row.patchedFields?.includes(field)) {
    return <span className={styles.diffAdded}>{children}</span>;
  }
  // Pass children through without an extra DOM node when there is no patch highlight.
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

const TABS = [
  'identity',
  'products',
  'buildings',
  'org',
  'associations',
  'financials',
  'coverage',
  'knownList',
  'findings',
] as const;

type DossierTab = typeof TABS[number];

function parseAliases(value: string): string[] {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function parseIdentifiers(value: string): WatchIdentifier[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [scheme, ...rest] = line.split(':');
      return { scheme: (scheme || 'other').trim(), value: rest.join(':').trim() };
    })
    .filter((item) => item.value);
}

function formatNumber(value: number | null): string {
  if (value == null) {
    return '—';
  }
  return value.toLocaleString();
}

function WatchTowerCompany(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    state, update, liveActions, refreshOpenCompany,
  } = useWatchTowerMock();
  const companyId = Number(id);
  const company = state.watchCompanies.find((item) => item.id === companyId);

  const tabParam = searchParams.get('tab');
  const tab: DossierTab = TABS.includes(tabParam as DossierTab)
    ? (tabParam as DossierTab)
    : 'identity';

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [uid, setUid] = useState('');
  const [aliases, setAliases] = useState('');
  const [identifiers, setIdentifiers] = useState('');
  const [addressCountry, setAddressCountry] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressPostCode, setAddressPostCode] = useState('');
  const [naceSection, setNaceSection] = useState('');
  const [naceCode, setNaceCode] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<WatchCompanyStatus>('draft');
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiChatId, setAiChatId] = useState(0);
  const [runsOpen, setRunsOpen] = useState(false);
  const [stepAttachment, setStepAttachment] = useState<IInvestigationStepAttachment | null>(null);

  useEffect(() => {
    if (!company) {
      return;
    }
    setName(company.name);
    setCode(company.code || '');
    setUid(company.uid);
    setAliases(company.aliases.join(', '));
    setIdentifiers(company.identifiers.map((item) => `${item.scheme}:${item.value}`).join('\n'));
    setAddressCountry(company.addressCountry);
    setAddressCity(company.addressCity || '');
    setAddressState(company.addressState || '');
    setAddressStreet(company.addressStreet || '');
    setAddressPostCode(company.addressPostCode || '');
    setNaceSection(company.naceSection || '');
    setNaceCode(company.naceCode || '');
    setWebsite(company.website || '');
    setNotes(company.notes || '');
    setStatus(company.status);
  }, [company]);

  const coverage = useMemo(
    () => state.schedules.filter((schedule) => schedule.watchCompanyId === companyId),
    [companyId, state.schedules],
  );

  const knownBuyers = useMemo(
    () => state.knownSupplierLinks.filter((link) => link.supplierWatchCompanyId === companyId),
    [companyId, state.knownSupplierLinks],
  );

  const knownSuppliers = useMemo(
    () => state.knownSupplierLinks.filter((link) => link.buyerWatchCompanyId === companyId),
    [companyId, state.knownSupplierLinks],
  );

  const sourceFindings = useMemo(
    () => state.findings.filter((finding) => (
      finding.watchCompanyId === companyId && !isInvestigationConclusion(finding)
    )),
    [companyId, state.findings],
  );
  const investigationFindings = useMemo(
    () => state.findings.filter((finding) => (
      finding.watchCompanyId === companyId && isInvestigationConclusion(finding)
    )),
    [companyId, state.findings],
  );

  const affectedFindingIds = useMemo(
    () => new Set(
      state.findingAffectedCompanies
        .filter((link) => link.affectedWatchCompanyId === companyId)
        .map((link) => link.findingId),
    ),
    [companyId, state.findingAffectedCompanies],
  );

  const affectedFindings = useMemo(
    () => state.findings.filter((finding) => affectedFindingIds.has(finding.id)),
    [affectedFindingIds, state.findings],
  );

  if (!companyId || Number.isNaN(companyId) || !company) {
    return <Navigate to={AppRoutes.watchTower} replace />;
  }

  const companyNameById = (otherId: number | null) => {
    if (otherId == null) {
      return t('targetedMarketing.none');
    }
    return state.watchCompanies.find((item) => item.id === otherId)?.name
      || String(otherId);
  };

  const openRelatedCompany = (otherId: number) => {
    navigate(generatePath(AppRoutes.watchTowerCompany, {
      id: String(otherId),
    }));
  };

  const relatedCompanyCell = (otherId: number | null) => {
    if (otherId == null) {
      return t('targetedMarketing.none');
    }
    return (
      <button
        type="button"
        className={styles.nameButton}
        onClick={() => openRelatedCompany(otherId)}
      >
        {companyNameById(otherId)}
      </button>
    );
  };

  const parentName = (parentId: number | null) => {
    if (parentId == null) {
      return t('targetedMarketing.none');
    }
    return company.orgNodes.find((node) => node.id === parentId)?.name
      || String(parentId);
  };

  const setTab = (next: DossierTab) => {
    setSearchParams({ tab: next }, { replace: true });
  };

  const reviewing = company.aiStatus === 'awaiting_human_review';
  const investigationLocked = reviewing
    || company.aiStatus === 'review_ongoing'
    || company.aiStatus === 'in_error';
  const suggestion = company.aiSuggestion;
  const productRows = reviewing
    ? reviewCollectionRows(company.products, suggestion, 'products')
    : currentCollectionRows(company.products);
  const buildingRows = reviewing
    ? reviewCollectionRows(company.buildings, suggestion, 'buildings')
    : currentCollectionRows(company.buildings);
  const orgRows = reviewing
    ? reviewCollectionRows(company.orgNodes, suggestion, 'orgNodes')
    : currentCollectionRows(company.orgNodes);
  const associationRows = reviewing
    ? reviewCollectionRows(company.associations, suggestion, 'associations')
    : currentCollectionRows(company.associations);
  const financialRows = reviewing
    ? reviewCollectionRows(company.financials, suggestion, 'financials')
    : currentCollectionRows(company.financials);
  const identifierFills = (suggestion?.fieldChanges || [])
    .filter((item) => item.path.startsWith('identifier.'));
  const extraIdentityFills = (suggestion?.fieldChanges || [])
    .filter((item) => [
      'ownership',
      'dateOpened',
      'dateClosed',
      'euTaxonomyCode',
      'addressBuilding',
      'addressCoordinates',
    ].includes(item.path));

  const onSave = (event: FormEvent) => {
    event.preventDefault();
    if (investigationLocked) {
      return;
    }
    const patch: Partial<WatchCompany> = {
      name: name.trim() || company.name,
      code: code.trim() || null,
      uid: uid.trim() || company.uid,
      aliases: parseAliases(aliases),
      identifiers: parseIdentifiers(identifiers),
      addressCountry: addressCountry.trim().toUpperCase(),
      addressCity: addressCity.trim() || null,
      addressState: addressState.trim() || null,
      addressStreet: addressStreet.trim() || null,
      addressPostCode: addressPostCode.trim() || null,
      naceSection: naceSection.trim() || null,
      naceCode: naceCode.trim() || null,
      website: website.trim() || null,
      notes: notes.trim() || null,
      status,
    };
    update((prev) => ({
      ...prev,
      watchCompanies: prev.watchCompanies.map((item) => (
        item.id === companyId ? { ...item, ...patch } : item
      )),
    }));
    toast.success(t('targetedMarketing.companySaved'));
  };

  const onExport = () => {
    downloadJsonFile(`watch-company-${company.uid}.json`, {
      schemaVersion: '1',
      exportedAt: new Date().toISOString(),
      sourceWatchCompanyId: company.id,
      companies: [toWatchCompanyImport(company)],
    });
    toast.success(t('targetedMarketing.exportStarted'));
  };

  const onTalkToAi = async () => {
    if (!company) {
      return;
    }
    try {
      const chat = await liveActions.openDossierChat(company.id, company.name);
      setAiChatId(chat.id);
      setAiChatOpen(true);
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  const onCopyStepToChat = async (attachment: IInvestigationStepAttachment) => {
    if (!company) {
      return;
    }
    setStepAttachment(attachment);
    if (aiChatOpen && aiChatId) {
      return;
    }
    try {
      const chat = await liveActions.openDossierChat(company.id, company.name);
      setAiChatId(chat.id);
      setAiChatOpen(true);
    } catch {
      setStepAttachment(null);
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  const onContinueInvestigation = async (runId: number) => {
    if (!company) {
      return;
    }
    try {
      await liveActions.continueInvestigation(company.id, runId);
      toast.success(t('targetedMarketing.investigationContinueQueued'));
    } catch (error) {
      toast.error(watchRequestErrorText(error) || t('targetedMarketing.liveRequestFailed'));
    }
  };

  const onAcceptInvestigation = async () => {
    if (!company) {
      return;
    }
    try {
      await liveActions.approveInvestigation(company.id);
      toast.success(t('targetedMarketing.investigationAccepted'));
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  const onRejectInvestigation = async () => {
    if (!company) {
      return;
    }
    try {
      await liveActions.cancelInvestigation(company.id);
      toast.success(t('targetedMarketing.investigationRejected'));
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  const onRetryInvestigation = async () => {
    if (!company) {
      return;
    }
    try {
      await liveActions.retryInvestigation(company.id);
      toast.success(t('targetedMarketing.investigationRetryQueued'));
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  const empty = (text?: string) => (
    <EmptyList text={text || t('targetedMarketing.noRows')} />
  );

  return (
    <TargetedMarketingGate>
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <TargetedMarketingHeader
            title={company.name}
            backTo={AppRoutes.watchTower}
            backLabel={t('targetedMarketing.watchTowerTitle')}
            actions={(
              <div className={styles.toolbarActions}>
                <Button
                  title={t('targetedMarketing.talkToAi')}
                  variant={BUTTON_VARIANT.secondary}
                  onClick={onTalkToAi}
                />
                <Button
                  title={t('targetedMarketing.investigationRunsTitle')}
                  variant={BUTTON_VARIANT.secondary}
                  onClick={() => setRunsOpen(true)}
                />
                <Button
                  title={t('targetedMarketing.exportCompany')}
                  variant={BUTTON_VARIANT.secondary}
                  onClick={onExport}
                />
                <Button
                  title={t('targetedMarketing.scheduleAgents')}
                  variant={BUTTON_VARIANT.secondary}
                  onClick={() => navigate(
                    `${AppRoutes.watchTowerSchedules}?companyId=${company.id}`,
                  )}
                />
              </div>
            )}
          />

          {company.aiStatus === 'review_ongoing' ? (
            <p className={styles.investigationBanner}>
              {t('targetedMarketing.investigationOngoing')}
            </p>
          ) : null}
          {company.aiStatus === 'in_error' ? (
            <div className={styles.investigationBanner}>
              <p>{company.aiErrorMessage || t('targetedMarketing.investigationError')}</p>
              <div className={styles.footerActions}>
                <Button
                  title={t('targetedMarketing.investigationRetry')}
                  onClick={onRetryInvestigation}
                />
                <Button
                  title={t('targetedMarketing.investigationCancel')}
                  variant={BUTTON_VARIANT.secondary}
                  onClick={onRejectInvestigation}
                />
              </div>
            </div>
          ) : null}
          {reviewing ? (
            <div className={styles.investigationBanner}>
              <p>{t('targetedMarketing.investigationReviewHint')}</p>
              <div className={styles.footerActions}>
                <Button
                  title={t('targetedMarketing.investigationApprove')}
                  onClick={onAcceptInvestigation}
                />
                <Button
                  title={t('targetedMarketing.investigationCancel')}
                  variant={BUTTON_VARIANT.secondary}
                  onClick={onRejectInvestigation}
                />
              </div>
            </div>
          ) : null}

          <Modal
            open={runsOpen}
            title={t('targetedMarketing.investigationRunsTitle')}
            className={styles.modalWide}
            isButtonsVisible={false}
            onClose={() => setRunsOpen(false)}
          >
            <div className={styles.runsModalBody}>
              <WatchTowerInvestigationRuns
                companyId={company.id}
                onCopyStep={(attachment) => {
                  setRunsOpen(false);
                  onCopyStepToChat(attachment).catch(() => undefined);
                }}
                onContinue={onContinueInvestigation}
              />
            </div>
          </Modal>

          <div className={styles.tabList} role="tablist">
            {TABS.map((item) => (
              <button
                key={item}
                type="button"
                role="tab"
                aria-selected={tab === item}
                className={`${styles.tabButton} ${tab === item ? styles.tabButtonActive : ''}`}
                onClick={() => setTab(item)}
              >
                {t(`targetedMarketing.${item}`)}
              </button>
            ))}
          </div>

          {tab === 'identity' ? (
            <Panel className={styles.panel}>
              <form className={styles.formGrid} onSubmit={onSave}>
                <div className={styles.field}>
                  <label htmlFor="wt-name">{t('targetedMarketing.name')}</label>
                  <input id="wt-name" value={name} onChange={(event) => setName(event.target.value)} />
                </div>
                <div className={styles.field}>
                  <InvestigationField
                    label={t('targetedMarketing.code')}
                    htmlFor="wt-code"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'code')}
                  >
                    <input
                      id="wt-code"
                      value={code}
                      disabled={investigationLocked}
                      onChange={(event) => setCode(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                <div className={styles.field}>
                  <label htmlFor="wt-uid">{t('targetedMarketing.uid')}</label>
                  <input id="wt-uid" value={uid} onChange={(event) => setUid(event.target.value)} />
                </div>
                <div className={styles.field}>
                  <label htmlFor="wt-status">{t('targetedMarketing.status')}</label>
                  <select
                    id="wt-status"
                    value={status}
                    onChange={(event) => setStatus(event.target.value as WatchCompanyStatus)}
                  >
                    <option value="draft">{t('common.draft')}</option>
                    <option value="active">{t('common.active')}</option>
                    <option value="archived">{t('targetedMarketing.archive')}</option>
                  </select>
                </div>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <InvestigationField
                    label={t('targetedMarketing.aliases')}
                    htmlFor="wt-aliases"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'aliases')}
                  >
                    <input
                      id="wt-aliases"
                      value={aliases}
                      disabled={investigationLocked}
                      onChange={(event) => setAliases(event.target.value)}
                      placeholder={t('targetedMarketing.aliasesHint')}
                    />
                  </InvestigationField>
                </div>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <label htmlFor="wt-identifiers">{t('targetedMarketing.identifiers')}</label>
                  <textarea
                    id="wt-identifiers"
                    value={identifiers}
                    disabled={investigationLocked}
                    onChange={(event) => setIdentifiers(event.target.value)}
                    placeholder="lei:5299…"
                  />
                  {reviewing ? identifierFills.map((change) => (
                    <p key={change.path} className={styles.diffAdded}>{change.after}</p>
                  )) : null}
                </div>
                <div className={styles.field}>
                  <label htmlFor="wt-country">{t('targetedMarketing.addressCountry')}</label>
                  <input
                    id="wt-country"
                    value={addressCountry}
                    onChange={(event) => setAddressCountry(event.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <InvestigationField
                    label={t('targetedMarketing.addressCity')}
                    htmlFor="wt-city"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'addressCity')}
                  >
                    <input
                      id="wt-city"
                      value={addressCity}
                      disabled={investigationLocked}
                      onChange={(event) => setAddressCity(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                <div className={styles.field}>
                  <InvestigationField
                    label={t('targetedMarketing.addressState')}
                    htmlFor="wt-state"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'addressState')}
                  >
                    <input
                      id="wt-state"
                      value={addressState}
                      disabled={investigationLocked}
                      onChange={(event) => setAddressState(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                <div className={styles.field}>
                  <InvestigationField
                    label={t('targetedMarketing.addressPostCode')}
                    htmlFor="wt-post"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'addressPostCode')}
                  >
                    <input
                      id="wt-post"
                      value={addressPostCode}
                      disabled={investigationLocked}
                      onChange={(event) => setAddressPostCode(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <InvestigationField
                    label={t('targetedMarketing.addressStreet')}
                    htmlFor="wt-street"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'addressStreet')}
                  >
                    <input
                      id="wt-street"
                      value={addressStreet}
                      disabled={investigationLocked}
                      onChange={(event) => setAddressStreet(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                <div className={styles.field}>
                  <InvestigationField
                    label={t('targetedMarketing.naceSection')}
                    htmlFor="wt-nace-s"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'naceSection')}
                  >
                    <input
                      id="wt-nace-s"
                      value={naceSection}
                      disabled={investigationLocked}
                      onChange={(event) => setNaceSection(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                <div className={styles.field}>
                  <InvestigationField
                    label={t('targetedMarketing.naceCode')}
                    htmlFor="wt-nace-c"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'naceCode')}
                  >
                    <input
                      id="wt-nace-c"
                      value={naceCode}
                      disabled={investigationLocked}
                      onChange={(event) => setNaceCode(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <InvestigationField
                    label={t('targetedMarketing.website')}
                    htmlFor="wt-web"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'website')}
                  >
                    <input
                      id="wt-web"
                      value={website}
                      disabled={investigationLocked}
                      onChange={(event) => setWebsite(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <InvestigationField
                    label={t('targetedMarketing.notes')}
                    htmlFor="wt-notes"
                    reviewing={reviewing}
                    change={fieldChange(suggestion, 'notes')}
                  >
                    <textarea
                      id="wt-notes"
                      value={notes}
                      disabled={investigationLocked}
                      onChange={(event) => setNotes(event.target.value)}
                    />
                  </InvestigationField>
                </div>
                {reviewing ? extraIdentityFills.map((change) => (
                  <InvestigationField
                    key={change.path}
                    label={change.path}
                    htmlFor={`wt-extra-${change.path}`}
                    className={`${styles.field} ${styles.fieldWide}`}
                    reviewing
                    change={change}
                  >
                    <span id={`wt-extra-${change.path}`}>{change.after}</span>
                  </InvestigationField>
                )) : null}
                {investigationLocked ? null : (
                  <div className={`${styles.footerActions} ${styles.fieldWide}`}>
                    <Button title={t('targetedMarketing.save')} type="submit" />
                  </div>
                )}
              </form>
            </Panel>
          ) : null}

          {tab === 'products' ? (
            <Panel className={styles.panel}>
              {productRows.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.name')}</th>
                        <th>{t('targetedMarketing.code')}</th>
                        <th>productCategory</th>
                        <th>productType</th>
                        <th>hsCode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productRows.map((row) => (
                        <tr
                          key={row.key}
                          className={rowDiffClass(row.op)}
                        >
                          <td>
                            {row.item.name}
                            {row.reason ? ` — ${row.reason}` : ''}
                          </td>
                          <td>
                            <ReviewCell row={row} field="code">
                              {row.item.code || t('targetedMarketing.none')}
                            </ReviewCell>
                          </td>
                          <td>
                            <ReviewCell row={row} field="productCategory">
                              {row.item.productCategory || t('targetedMarketing.none')}
                            </ReviewCell>
                          </td>
                          <td>{row.item.productType || t('targetedMarketing.none')}</td>
                          <td>
                            <ReviewCell row={row} field="hsCode">
                              {row.item.hsCode || t('targetedMarketing.none')}
                            </ReviewCell>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          ) : null}

          {tab === 'buildings' ? (
            <Panel className={styles.panel}>
              {buildingRows.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.name')}</th>
                        <th>assetCategory</th>
                        <th>{t('targetedMarketing.addressCountry')}</th>
                        <th>{t('targetedMarketing.addressCity')}</th>
                        <th>{t('targetedMarketing.addressStreet')}</th>
                        <th>ownedLeased</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buildingRows.map((row) => (
                        <tr
                          key={row.key}
                          className={rowDiffClass(row.op)}
                        >
                          <td>
                            {row.item.name}
                            {row.reason ? ` — ${row.reason}` : ''}
                          </td>
                          <td>{(row.item.assetCategory || []).join(', ') || t('targetedMarketing.none')}</td>
                          <td>
                            <ReviewCell row={row} field="addressCountry">
                              {row.item.addressCountry || t('targetedMarketing.none')}
                            </ReviewCell>
                          </td>
                          <td>
                            <ReviewCell row={row} field="addressCity">
                              {row.item.addressCity || t('targetedMarketing.none')}
                            </ReviewCell>
                          </td>
                          <td>
                            <ReviewCell row={row} field="addressStreet">
                              {row.item.addressStreet || t('targetedMarketing.none')}
                            </ReviewCell>
                          </td>
                          <td>{row.item.ownedLeased || t('targetedMarketing.none')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          ) : null}

          {tab === 'org' ? (
            <Panel className={styles.panel}>
              {orgRows.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.name')}</th>
                        <th>role</th>
                        <th>parent</th>
                        <th>{t('targetedMarketing.addressCountry')}</th>
                        <th>ownership %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgRows.map((row) => (
                        <tr key={row.key} className={rowDiffClass(row.op)}>
                          <td>
                            {row.item.name}
                            {row.reason ? ` — ${row.reason}` : ''}
                          </td>
                          <td>{row.item.role}</td>
                          <td>{parentName(row.item.parentId)}</td>
                          <td>
                            <ReviewCell row={row} field="addressCountry">
                              {row.item.addressCountry || t('targetedMarketing.none')}
                            </ReviewCell>
                          </td>
                          <td>
                            {row.item.ownershipPct == null
                              ? t('targetedMarketing.none')
                              : `${row.item.ownershipPct}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          ) : null}

          {tab === 'associations' ? (
            <Panel className={styles.panel}>
              {associationRows.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.eventKind')}</th>
                        <th>{t('targetedMarketing.occurredOn')}</th>
                        <th>{t('targetedMarketing.person')}</th>
                        <th>{t('targetedMarketing.relatedCompany')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {associationRows.map((row) => (
                        <tr key={row.key} className={rowDiffClass(row.op)}>
                          <td>
                            {t(`targetedMarketing.eventKind_${row.item.eventKind}`, {
                              defaultValue: row.item.eventKind,
                            })}
                          </td>
                          <td>{formatIsoDate(row.item.occurredOn)}</td>
                          <td>
                            <ReviewCell row={row} field="personRole">
                              {row.item.personFullName
                                ? `${row.item.personFullName} (${row.item.personRole || row.item.personUid
                                  || t('targetedMarketing.none')})`
                                : t('targetedMarketing.none')}
                            </ReviewCell>
                          </td>
                          <td>{relatedCompanyCell(row.item.relatedWatchCompanyId)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          ) : null}

          {tab === 'financials' ? (
            <Panel className={styles.panel}>
              {financialRows.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.period')}</th>
                        <th>{t('targetedMarketing.revenue')}</th>
                        <th>{t('targetedMarketing.ebitda')}</th>
                        <th>{t('targetedMarketing.liquidity')}</th>
                        <th>{t('targetedMarketing.marketValuation')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financialRows.map((row) => (
                        <tr key={row.key} className={rowDiffClass(row.op)}>
                          <td>
                            {`${formatIsoDate(row.item.periodStart)} – ${formatIsoDate(row.item.periodEnd)}`}
                          </td>
                          <td>
                            <ReviewCell row={row} field="revenue">
                              {formatNumber(row.item.revenue)}
                            </ReviewCell>
                          </td>
                          <td>{formatNumber(row.item.ebitda)}</td>
                          <td>{formatNumber(row.item.liquidity)}</td>
                          <td>{formatNumber(row.item.marketValuation)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          ) : null}

          {tab === 'coverage' ? (
            <Panel className={styles.panel}>
              {coverage.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.schedule')}</th>
                        <th>{t('targetedMarketing.agents')}</th>
                        <th>{t('targetedMarketing.target')}</th>
                        <th>{t('targetedMarketing.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverage.map((schedule) => (
                        <tr key={schedule.id}>
                          <td>{schedule.title}</td>
                          <td>{formatAgentType(schedule.type)}</td>
                          <td>{schedule.targetKind}</td>
                          <td>{schedule.paused ? 'paused' : schedule.lastRunStatus || t('targetedMarketing.none')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          ) : null}

          {tab === 'knownList' ? (
            <Panel className={styles.panel}>
              <h3 className={styles.sectionTitle}>{t('targetedMarketing.knownBuyers')}</h3>
              {knownBuyers.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.name')}</th>
                        <th>addedBy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {knownBuyers.map((link) => (
                        <tr key={link.id}>
                          <td>
                            <button
                              type="button"
                              className={styles.nameButton}
                              onClick={() => navigate(generatePath(
                                AppRoutes.watchTowerCompany,
                                { id: String(link.buyerWatchCompanyId) },
                              ))}
                            >
                              {companyNameById(link.buyerWatchCompanyId)}
                            </button>
                          </td>
                          <td>{link.addedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <h3 className={styles.sectionTitle}>{t('targetedMarketing.knownSuppliers')}</h3>
              {knownSuppliers.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.name')}</th>
                        <th>addedBy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {knownSuppliers.map((link) => (
                        <tr key={link.id}>
                          <td>
                            <button
                              type="button"
                              className={styles.nameButton}
                              onClick={() => navigate(generatePath(
                                AppRoutes.watchTowerCompany,
                                { id: String(link.supplierWatchCompanyId) },
                              ))}
                            >
                              {companyNameById(link.supplierWatchCompanyId)}
                            </button>
                          </td>
                          <td>{link.addedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          ) : null}

          {tab === 'findings' ? (
            <Panel className={styles.panel}>
              <h3 className={styles.sectionTitle}>{t('targetedMarketing.investigationConclusions')}</h3>
              {investigationFindings.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.severity')}</th>
                        <th>{t('targetedMarketing.watchTowerKind')}</th>
                        <th>{t('targetedMarketing.title')}</th>
                        <th>{t('targetedMarketing.relatedCompany')}</th>
                        <th>{t('targetedMarketing.occurredOn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {investigationFindings.map((finding) => {
                        const kind = watchTowerKind(finding);
                        return (
                          <tr key={finding.id}>
                            <td>{finding.severity}</td>
                            <td>
                              {kind
                                ? t(`targetedMarketing.watchTowerKind_${kind}`, {
                                  defaultValue: kind,
                                })
                                : t('targetedMarketing.findingType_investigation_conclusion')}
                            </td>
                            <td>{finding.title}</td>
                            <td>{relatedCompanyCell(relatedWatchCompanyIdFromFinding(finding))}</td>
                            <td>{formatIsoDate(finding.observedAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <h3 className={styles.sectionTitle}>{t('targetedMarketing.sourceFindings')}</h3>
              {sourceFindings.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.severity')}</th>
                        <th>{t('targetedMarketing.title')}</th>
                        <th>{t('targetedMarketing.occurredOn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sourceFindings.map((finding) => (
                        <tr key={finding.id}>
                          <td>{finding.severity}</td>
                          <td>{finding.title}</td>
                          <td>{formatIsoDate(finding.observedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <h3 className={styles.sectionTitle}>{t('targetedMarketing.affectedFindings')}</h3>
              {affectedFindings.length === 0 ? empty() : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('targetedMarketing.severity')}</th>
                        <th>{t('targetedMarketing.title')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affectedFindings.map((finding) => (
                        <tr key={finding.id}>
                          <td>{finding.severity}</td>
                          <td>{finding.title}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          ) : null}
        </div>
      </div>
      {aiChatOpen && aiChatId ? (
        <WatchTowerDossierChatDialog
          open={aiChatOpen}
          chatId={aiChatId}
          company={company}
          state={state}
          onClose={() => {
            setAiChatOpen(false);
            setStepAttachment(null);
            refreshOpenCompany().catch(() => undefined);
          }}
          onApply={update}
          onRerunInvestigation={() => liveActions.retryInvestigation(company.id)}
          stepAttachment={stepAttachment}
          onClearStep={() => setStepAttachment(null)}
        />
      ) : null}
    </TargetedMarketingGate>
  );
}

export default WatchTowerCompany;
